/**
 * Variant Quantity Limit
 * -----------------------
 * Enforces a maximum quantity of 1 for specific variants on the PDP —
 * both in the quantity selector (single add) AND against what's already
 * in the cart (repeated adds), so clicking "Add to cart" more than once
 * can't push the cart total past MAX_QTY for a restricted variant.
 *
 * To restrict a product, add the 'qty-limit-1' tag to it in Shopify admin.
 * No code change is needed.
 */
(function () {
  if (window.__variantQtyLimitInit) return;
  window.__variantQtyLimitInit = true;

  // === CONFIG ===
  // Variant IDs come from snippets/buy-buttons.liquid, which only loads this
  // script on products tagged 'qty-limit-1' (all variants of the product).
  var RESTRICTED_VARIANTS = window.variantQtyLimitIds || [];
  var MAX_QTY = 1;
  var MESSAGE = 'Only 1 quantity allowed for this product';
  var CART_MAX_MESSAGE = 'You already have the maximum quantity of this product in your cart';
  // ================================================

  // Latest cart snapshot, kept in sync via the site-wide 'cart_updated' event
  // (assets/custom-event-cart-update.js, loaded in sections/header.liquid) —
  // it fires once on page load and again after every /cart/* request, so no
  // extra /cart.js fetch is needed here.
  var cartSnapshot = null;

  function isRestricted(variantId) {
    variantId = parseInt(variantId, 10);
    return RESTRICTED_VARIANTS.indexOf(variantId) !== -1;
  }

  // Read the currently selected variant ID from the main product form.
  function getSelectedVariantId() {
    var input = document.querySelector('[data-type="add-to-cart-form"] input[name="id"]');
    return input ? input.value : null;
  }

  // Sum how much of a given variant is already sitting in the cart.
  function qtyInCart(variantId) {
    variantId = parseInt(variantId, 10);
    if (!cartSnapshot || !cartSnapshot.items) return 0;
    var total = 0;
    cartSnapshot.items.forEach(function (item) {
      if (item.variant_id === variantId) total += item.quantity;
    });
    return total;
  }

  function getSubmitButtons() {
    return document.querySelectorAll('[data-type="add-to-cart-form"] button[type="submit"]');
  }

  // Single floating toast (fixed to the viewport, does not affect layout).
  function showToast(message) {
    var toast = document.querySelector('.variant-qty-limit__toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'variant-qty-limit__toast';
      toast.setAttribute('role', 'status');
      document.body.appendChild(toast);
    }
    toast.textContent = message || MESSAGE;
    // Force reflow so the transition runs even on the first show.
    void toast.offsetWidth;
    toast.classList.add('is-visible');
    clearTimeout(toast.__hideTimer);
    toast.__hideTimer = setTimeout(function () {
      toast.classList.remove('is-visible');
    }, 2500);
  }

  // Apply / remove the limit on every quantity selector on the page,
  // based on the currently selected variant. Also disables the add-to-cart
  // submit button(s) once the cart already holds MAX_QTY of this variant,
  // so repeated clicks (each with qty=1) can't keep adding it.
  function applyLimit() {
    var variantId = getSelectedVariantId();
    var restricted = isRestricted(variantId);
    var atCartMax = restricted && qtyInCart(variantId) >= MAX_QTY;

    document.querySelectorAll('.product-form__quantity .quantity__input').forEach(function (input) {
      if (restricted) {
        input.setAttribute('max', MAX_QTY);
        if (parseInt(input.value, 10) > MAX_QTY) {
          input.value = MAX_QTY;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else {
        input.removeAttribute('max');
      }
    });

    getSubmitButtons().forEach(function (btn) {
      if (atCartMax) {
        btn.setAttribute('data-qty-limit-disabled', 'true');
        btn.setAttribute('aria-disabled', 'true');
        btn.disabled = true;
      } else if (btn.getAttribute('data-qty-limit-disabled') === 'true') {
        btn.removeAttribute('data-qty-limit-disabled');
        btn.removeAttribute('aria-disabled');
        btn.disabled = false;
      }
    });
  }

  function init() {
    // Stay in sync with live cart state — 'cart_updated' (fired by
    // assets/custom-event-cart-update.js) runs once on page load and again
    // after every /cart/* request, so this always reflects what's actually
    // in the cart, including items added/removed via the cart drawer.
    window.addEventListener('cart_updated', function (e) {
      cartSnapshot = e.detail;
      applyLimit();
    });

    // Also seed the cart directly on init, instead of only waiting on the
    // event above — that event fires from its own async fetch, so without
    // this there's a brief window right after page load where cartSnapshot
    // is still null and a restricted variant already at its cap wouldn't
    // be caught yet.
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        cartSnapshot = cart;
        applyLimit();
      })
      .catch(function () {});

    applyLimit();

    // Hard stop: block the actual add-to-cart submit if this variant is
    // already at its cap in the cart. This is the real fix for repeated
    // "Add to cart" clicks (qty selector shows 1 each time, but clicking
    // multiple times used to keep adding 1 more) — the button-disable in
    // applyLimit() is the primary UX, this is a defense-in-depth backstop
    // in case the button's disabled state is momentarily stale.
    document.addEventListener(
      'submit',
      function (e) {
        var form = e.target.closest && e.target.closest('[data-type="add-to-cart-form"]');
        if (!form) return;
        var variantId = getSelectedVariantId();
        if (isRestricted(variantId) && qtyInCart(variantId) >= MAX_QTY) {
          e.preventDefault();
          e.stopImmediatePropagation();
          showToast(CART_MAX_MESSAGE);
        }
      },
      true
    );

    // Show the toast whenever the plus button is pressed on a restricted variant.
    // (The native max attribute already blocks the increment; this only messages.)
    document.addEventListener(
      'click',
      function (e) {
        var plusBtn = e.target.closest('.product-form__quantity button[name="plus"]');
        if (!plusBtn) return;
        if (!isRestricted(getSelectedVariantId())) return;

        showToast();
      },
      true
    );

    // Re-clamp if the shopper types a value manually.
    document.addEventListener('change', function (e) {
      if (!e.target.matches || !e.target.matches('.product-form__quantity .quantity__input')) return;
      if (!isRestricted(getSelectedVariantId())) return;
      if (parseInt(e.target.value, 10) > MAX_QTY) {
        e.target.value = MAX_QTY;
        showToast();
      }
    });

    // Variant selectors (radios/dropdowns/swatches) bubble a change event.
    // A variant change can dispatch more than one 'change' event in the same
    // tick (the radio's own change, plus a synthetic one global.js fires on
    // the hidden variant-id input), so this is debounced to a single call.
    // (No MutationObserver here: global.js sets the hidden id input's value
    // as a JS property, not an HTML attribute, so an attribute-watching
    // observer would never fire — this change listener is what actually
    // catches every variant switch.)
    var applyLimitTimer;
    document.addEventListener('change', function (e) {
      if (e.target.closest('variant-selects, variant-radios, .product-form__input')) {
        clearTimeout(applyLimitTimer);
        applyLimitTimer = setTimeout(applyLimit, 0);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
